import React, { useCallback, useEffect, useState } from 'react';
import { api, federation } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';
import { track, ACTION_ASSIGNED } from '../../utils/analytics';
import FederationGate from './FederationGate';
import {
  ACTION_STATUSES,
  ErrorBanner,
  LoadingBlock,
  PageHeader,
  btnPrimary,
  canWrite,
  inputClass,
  isViewer,
  safeText,
} from './federationShared';

function ActionsInner() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const viewer = isViewer(user);

  const [actions, setActions] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    programId: '',
    teamId: '',
    findingId: '',
    status: 'Not started',
  });

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (statusFilter) params.status = statusFilter;
    Promise.all([
      federation.getActions(params),
      federation.getPrograms().catch(() => ({ programs: [] })),
      api.getTeams().catch(() => ({ teams: [] })),
      federation.getFindings().catch(() => ({ findings: [] })),
    ])
      .then(([a, p, t, f]) => {
        setActions(a.actions || []);
        setPrograms(p.programs || []);
        setTeams(t.teams || []);
        setFindings(f.findings || []);
      })
      .catch((e) => {
        setActions([]);
        setError(e.message || 'Could not load actions');
      })
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!writable) return;
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await federation.createAction({
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        programId: form.programId || undefined,
        teamId: form.teamId || undefined,
        findingId: form.findingId || undefined,
        status: form.status,
      });
      track(ACTION_ASSIGNED, { status: form.status });
      setForm({
        title: '',
        description: '',
        programId: '',
        teamId: '',
        findingId: '',
        status: 'Not started',
      });
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message || 'Could not create action');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (action, status) => {
    if (!writable) return;
    setError('');
    try {
      await federation.updateAction({ id: action.id, status });
      load();
    } catch (err) {
      setError(err.message || 'Could not update action');
    }
  };

  return (
    <>
      <PageHeader
        title="Actions"
        description="Coaching actions linked to Federation findings and programs."
        actions={
          writable && !viewer ? (
            <button type="button" className={btnPrimary} onClick={() => setShowForm((v) => !v)}>
              {showForm ? 'Cancel' : 'Create action'}
            </button>
          ) : null
        }
      />

      <div className="mb-6 max-w-xs">
        <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
        <select
          className={inputClass}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {ACTION_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <ErrorBanner message={error} onRetry={load} />

      {showForm && writable && (
        <form onSubmit={handleCreate} className="border border-navy-200 rounded-xl bg-white p-5 mb-6 space-y-4 max-w-xl">
          <h2 className="text-base font-semibold text-navy-900">New organizational action</h2>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Source finding</label>
            <select className={inputClass} value={form.findingId} onChange={(e) => setForm((f) => ({ ...f, findingId: e.target.value }))}>
              <option value="">Optional organizational finding</option>
              {findings.map((finding) => (
                <option key={finding.id} value={finding.id}>{safeText(finding.findingText).slice(0, 90)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Title</label>
            <input
              className={inputClass}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              maxLength={300}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Description</label>
            <textarea
              className={inputClass}
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={4000}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Program</label>
              <select
                className={inputClass}
                value={form.programId}
                onChange={(e) => setForm((f) => ({ ...f, programId: e.target.value }))}
              >
                <option value="">Optional</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>{safeText(p.name)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Team</label>
              <select
                className={inputClass}
                value={form.teamId}
                onChange={(e) => setForm((f) => ({ ...f, teamId: e.target.value }))}
              >
                <option value="">Optional</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{safeText(t.name)}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Status</label>
            <select
              className={inputClass}
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              {ACTION_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={saving} className={btnPrimary}>
            {saving ? 'Creating…' : 'Create action'}
          </button>
        </form>
      )}

      {loading && <LoadingBlock label="Loading actions…" />}

      {!loading && actions.length === 0 && !error && (
        <div className="border border-navy-200 rounded-xl bg-white px-6 py-10 text-center">
          <p className="text-navy-900 font-medium">No coaching actions</p>
          <p className="text-sm text-gray-500 mt-2">
            Create actions from findings or add them here to track coaching follow-through.
          </p>
        </div>
      )}

      {!loading && actions.length > 0 && (
        <ul className="space-y-3">
          {actions.map((action) => (
            <li key={action.id} className="border border-navy-200 rounded-xl bg-white px-4 py-4">
              <p className="font-medium text-navy-900">{safeText(action.title)}</p>
              {action.description ? (
                <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{safeText(action.description)}</p>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                <span>Status: {safeText(action.status)}</span>
                {action.priority ? <span>Priority: {safeText(action.priority)}</span> : null}
                {action.dueDate ? <span>Due: {safeText(action.dueDate)}</span> : null}
                <span>{safeText(action.createdAt)}</span>
              </div>
              {writable && (
                <div className="mt-3 max-w-xs">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Update status</label>
                  <select
                    className={inputClass}
                    value={action.status || 'Not started'}
                    onChange={(e) => handleStatusChange(action, e.target.value)}
                  >
                    {ACTION_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function FederationActions() {
  return (
    <FederationGate>
      <ActionsInner />
    </FederationGate>
  );
}
